//Instructor Signup
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { createInstructorAccount } from '../../services/auth';
import { getAnimals } from '../../services/firestore';
import Button from '../common/Button';
import { Animal } from '../../types';
import { ASSET_PATHS } from '../../utils/gameConfig';
import AnimalSelector from '../animals/AnimalSelector';

interface InstructorSignupProps {
  onBack: () => void;
}

interface InstructorFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  username: string;
  selectedAnimalId: string;
}

const InstructorSignup: React.FC<InstructorSignupProps> = ({ onBack }) => {
  const dispatch = useDispatch();
  const { animals } = useSelector((state: RootState) => state.game);
  
  const [formData, setFormData] = useState<InstructorFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    username: '',
    selectedAnimalId: animals.length > 0 ? animals[0].id : '',
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<'info' | 'animal' | 'success'>(animals.length > 0 ? 'info' : 'info');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    //Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    //Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    //Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    //Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (animals.length === 0) {
      //Load animals if not already loaded
      try {
        const loadedAnimals = await getAnimals();
        if (loadedAnimals.length > 0) {
          setFormData(prev => ({ ...prev, selectedAnimalId: loadedAnimals[0].id }));
          setStep('animal');
        }
      } catch (error) {
        setErrors({ submit: 'Failed to load animals. Please try again.' });
      }
    } else {
      setStep('animal');
    }
  };

  const handleFinalSubmit = async () => {
    if (!formData.selectedAnimalId) {
      setErrors({ animal: 'Please select an animal guide' });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      await createInstructorAccount({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        username: formData.username.trim(),
        animalId: formData.selectedAnimalId,
      });

      setStep('success');
    } catch (error: any) {
      console.error('Instructor signup error:', error);
      setErrors({ 
        submit: error.message || 'Failed to create instructor account. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="min-h-screen flex items-center justify-center p-6"
        style={{
          backgroundImage: `url(${ASSET_PATHS.BACKGROUNDS.LOGIN})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-powder-200 p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-powder-800 mb-4">Registration Submitted!</h2>
          <p className="text-powder-600 mb-6">
            Your instructor account has been created and is pending approval. 
            You'll receive an email notification once your account is activated.
          </p>
          <Button
            variant="primary"
            onClick={onBack}
            fullWidth
          >
            Return to Login
          </Button>
        </div>
      </motion.div>
    );
  }

  if (step === 'animal') {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="min-h-screen flex items-center justify-center p-6"
        style={{
          backgroundImage: `url(${ASSET_PATHS.BACKGROUNDS.LOGIN})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-powder-200 p-8 max-w-lg w-full">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-powder-800 mb-2">Choose Your Guide</h2>
            <p className="text-powder-600">
              Select an animal guide to represent you in your forest classrooms
            </p>
          </div>

          <AnimalSelector
            animals={animals}
            selectedAnimalId={formData.selectedAnimalId}
            onSelect={(animalId) => setFormData(prev => ({ ...prev, selectedAnimalId: animalId }))}
            error={errors.animal}
          />

          {errors.animal && (
            <p className="text-red-500 text-sm text-center mb-4">{errors.animal}</p>
          )}

          <div className="flex space-x-4">
            <Button
              variant="outline"
              onClick={() => setStep('info')}
              disabled={isSubmitting}
              fullWidth
            >
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleFinalSubmit}
              disabled={isSubmitting || !formData.selectedAnimalId}
              fullWidth
            >
              {isSubmitting ? 'Creating Account...' : 'Create Instructor Account'}
            </Button>
          </div>

          {errors.submit && (
            <p className="text-red-500 text-sm text-center mt-4">{errors.submit}</p>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        backgroundImage: `url(${ASSET_PATHS.BACKGROUNDS.LOGIN})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-powder-200 p-8 max-w-lg w-full">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-powder-800 mb-2">Instructor Registration</h2>
          <p className="text-powder-600">
            Create an instructor account to manage forest classrooms
          </p>
        </div>

        <form onSubmit={handleInfoSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-powder-600 mb-2">
                First Name
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                className="w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
                placeholder="Jane"
              />
              {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-powder-600 mb-2">
                Last Name
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                className="w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
                placeholder="Smith"
              />
              {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-powder-600 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
              placeholder="jane.smith@school.edu"
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-powder-600 mb-2">
              Username
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
              className="w-full px-3 py-2 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
              placeholder="mrs_smith"
            />
            {errors.username && <p className="text-red-500 text-sm mt-1">{errors.username}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-powder-600 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="w-full px-3 py-2 pr-10 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
                placeholder="At least 8 characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-powder-400 hover:text-powder-600 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-powder-600 mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className="w-full px-3 py-2 pr-10 border border-powder-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500"
                placeholder="Confirm your password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-powder-400 hover:text-powder-600 transition-colors"
                tabIndex={-1}
              >
                {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
          </div>

          <div className="flex space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              fullWidth
            >
              Back to Login
            </Button>
            <Button
              type="submit"
              variant="primary"
              fullWidth
            >
              Continue
            </Button>
          </div>

          {errors.submit && (
            <p className="text-red-500 text-sm text-center">{errors.submit}</p>
          )}
        </form>
      </div>
    </motion.div>
  );
};

export default InstructorSignup;